import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useSearch } from "../../../contexts/SearchContext";
import { fetchAdminMessagesActive } from "../api/adminMessagesApi";
import { fetchRemindersSummary } from "../api/remindersSummaryApi";

const ADMIN_BROADCAST_TRACE =
  typeof import.meta !== "undefined" &&
  (Boolean(import.meta.env?.DEV) ||
    String(import.meta.env?.VITE_REMINDERS_BROADCAST_TRACE || "").trim() === "1");

const RemindersHubContext = createContext(null);

export function RemindersHubProvider({ children }) {
  const location = useLocation();
  const { loading: agentSearchResultsLoading } = useSearch();
  const deferHubRefreshWhileSearching =
    typeof location.pathname === "string" &&
    location.pathname.includes("searchoffer") &&
    agentSearchResultsLoading;

  const [summary, setSummary] = useState({
    personal_open: 0,
    admin_feed_unread: 0,
    operation_unread: 0,
    operation_unread_agent: 0,
    operation_unread_admin: 0,
    operation_first_unread: null,
    operation_open: 0,
    badge_total: 0,
  });
  const [activeAdmin, setActiveAdmin] = useState({
    popups: [],
    banners: [],
    feed: [],
    counts: {},
  });
  const [loadingAdmin, setLoadingAdmin] = useState(false);
  const [skipPopupIds, setSkipPopupIds] = useState([]);

  const refreshSummary = useCallback(async () => {
    if (deferHubRefreshWhileSearching) return;
    try {
      const { data } = await fetchRemindersSummary();
      if (import.meta.env?.DEV) {
        // Temporary: diagnose empty ops bell for dual-role / agent sessions.
        // eslint-disable-next-line no-console
        console.info("[REMINDERS_SUMMARY_OPS_BADGE]", {
          operation_unread: data?.operation_unread ?? 0,
          operation_unread_agent: data?.operation_unread_agent ?? 0,
          operation_unread_admin: data?.operation_unread_admin ?? 0,
          operation_first_unread: data?.operation_first_unread ?? null,
          badge_total: data?.badge_total ?? 0,
        });
      }
      const next = {
        personal_open: 0,
        admin_feed_unread: data?.admin_feed_unread ?? 0,
        operation_unread: data?.operation_unread ?? 0,
        operation_unread_agent: data?.operation_unread_agent ?? 0,
        operation_unread_admin: data?.operation_unread_admin ?? 0,
        operation_first_unread: data?.operation_first_unread ?? null,
        operation_open: data?.operation_open ?? 0,
        badge_total: data?.badge_total ?? data?.admin_feed_unread ?? 0,
      };
      setSummary((prev) =>
        prev.admin_feed_unread === next.admin_feed_unread &&
        prev.operation_unread === next.operation_unread &&
        prev.operation_unread_agent === next.operation_unread_agent &&
        prev.operation_unread_admin === next.operation_unread_admin &&
        prev.operation_open === next.operation_open &&
        prev.badge_total === next.badge_total &&
        JSON.stringify(prev.operation_first_unread) ===
          JSON.stringify(next.operation_first_unread)
          ? prev
          : next
      );
    } catch {
      const fallback = {
        personal_open: 0,
        admin_feed_unread: 0,
        operation_unread: 0,
        operation_unread_agent: 0,
        operation_unread_admin: 0,
        operation_first_unread: null,
        operation_open: 0,
        badge_total: 0,
      };
      setSummary((prev) =>
        prev.admin_feed_unread === fallback.admin_feed_unread &&
        prev.operation_unread === fallback.operation_unread &&
        prev.operation_unread_agent === fallback.operation_unread_agent &&
        prev.operation_unread_admin === fallback.operation_unread_admin &&
        prev.operation_open === fallback.operation_open &&
        prev.badge_total === fallback.badge_total &&
        prev.operation_first_unread == null
          ? prev
          : fallback
      );
    }
  }, [deferHubRefreshWhileSearching]);

  const refreshActiveAdmin = useCallback(async () => {
    if (deferHubRefreshWhileSearching) return;
    setLoadingAdmin(true);
    try {
      const { data } = await fetchAdminMessagesActive();
      setActiveAdmin({
        popups: data?.popups || [],
        banners: data?.banners || [],
        feed: data?.feed || [],
        counts: data?.counts || {},
      });
      if (ADMIN_BROADCAST_TRACE) {
        // eslint-disable-next-line no-console
        console.info("[ADMIN_BROADCAST_RENDER_TRACE]", {
          popups: (data?.popups || []).length,
          banners: (data?.banners || []).length,
          feedItems: (data?.feed || []).length,
          counts: data?.counts || {},
        });
      }
    } catch (e) {
      if (ADMIN_BROADCAST_TRACE) {
        // eslint-disable-next-line no-console
        console.info("[ADMIN_BROADCAST_RENDER_TRACE]", {
          error: "fetch_failed",
          message: String(e?.message || e),
        });
      }
      setActiveAdmin({ popups: [], banners: [], feed: [], counts: {} });
    } finally {
      setLoadingAdmin(false);
    }
  }, [deferHubRefreshWhileSearching]);

  useEffect(() => {
    if (deferHubRefreshWhileSearching) return;
    refreshSummary();
    refreshActiveAdmin();
  }, [deferHubRefreshWhileSearching, refreshSummary, refreshActiveAdmin]);

  useEffect(() => {
    if (deferHubRefreshWhileSearching) return undefined;
    const interval = setInterval(() => {
      void refreshSummary();
    }, 30000);
    const onOpsUnreadChanged = () => {
      void refreshSummary();
    };
    window.addEventListener("operationRequestUnreadChanged", onOpsUnreadChanged);
    return () => {
      clearInterval(interval);
      window.removeEventListener(
        "operationRequestUnreadChanged",
        onOpsUnreadChanged
      );
    };
  }, [deferHubRefreshWhileSearching, refreshSummary]);

  const visiblePopups = useMemo(() => {
    const skip = new Set((skipPopupIds || []).map((x) => String(x)));
    return (activeAdmin.popups || []).filter((p) => !skip.has(String(p.id)));
  }, [activeAdmin.popups, skipPopupIds]);
  const currentPopup = visiblePopups[0] || null;

  const optimisticSkipPopup = useCallback((id) => {
    const key = String(id);
    setSkipPopupIds((s) => (s.map(String).includes(key) ? s : [...s, key]));
  }, []);

  const revertOptimisticPopupSkip = useCallback((id) => {
    const key = String(id);
    setSkipPopupIds((s) => s.filter((x) => String(x) !== key));
  }, []);

  const value = useMemo(
    () => ({
      summary,
      refreshSummary,
      activeAdmin,
      refreshActiveAdmin,
      loadingAdmin,
      currentPopup,
      optimisticSkipPopup,
      revertOptimisticPopupSkip,
    }),
    [
      summary,
      refreshSummary,
      activeAdmin,
      refreshActiveAdmin,
      loadingAdmin,
      currentPopup,
      optimisticSkipPopup,
      revertOptimisticPopupSkip,
    ]
  );

  return (
    <RemindersHubContext.Provider value={value}>{children}</RemindersHubContext.Provider>
  );
}

export function useRemindersHub() {
  const ctx = useContext(RemindersHubContext);
  if (!ctx) {
    throw new Error("useRemindersHub must be used within RemindersHubProvider");
  }
  return ctx;
}

export function useRemindersHubOptional() {
  return useContext(RemindersHubContext);
}
